import { html, PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { LandingPageContentLoader } from "./landing-page-content-loader";
import { openModule } from "../../modules/global_function"
import { BaseWidget } from "./base-widget";
import "./landing-page-content-loader";

// Workspace widget implementation.  The contents of workspace widgets should be loaded from another URL.  Some workspace widgets
// may have to auto refresh on specified intervals
export class DataInsightsWidget extends BaseWidget {
    private _contentUrl: string = '';        // The URL that is used for populating the widget content
    private _refreshInterval: Number = 0;    // If the WS widget needs to be refreshed at specified intervals, this is the time in millis
    private _refreshCount: Number = 0;       // Just for debugging, this gives the count of auto refresh.  Gets reset if content is modified by user intervention
    private timerHandler: { handle?: any; }; // For internal use only, keep the timer handle to cancel if required
    private valid: boolean;                  // For internal use only, if the widget is removed, we need to stop the timer.


    get contentUrl(): string {
        return this._contentUrl;
    }

    @property({ type: String })
    set contentUrl(value: string) {
        // The content URL is changing, so stop the timer.  Will restart it with new URL later
        if (this.timerHandler?.handle) {
            clearTimeout(this.timerHandler.handle);
            this.timerHandler.handle = 0;
        }
        this.refreshCount = 0; // Reset the counter
        const oldValue = this._contentUrl;
        this._contentUrl = value;
        this.requestUpdate('contentUrl', oldValue);

        // Restart the timer if refresh interval is set
        this.setNextRefresh();
    }

    get refreshInterval(): Number {
        return this._refreshInterval;
    }

    @property({ type: Number })
    set refreshInterval(value: Number) {
        const oldValue = this._refreshInterval;
        this._refreshInterval = value;
        this.requestUpdate('refreshInterval', oldValue);
    }

    get refreshCount(): Number {
        return this._refreshCount;
    }

    @property({ type: Number })
    set refreshCount(value: Number) {
        const oldValue = this._refreshCount;
        this._refreshCount = value;
        this.requestUpdate('refreshCount', oldValue);
    }
    // End getters and setters for properties

    // Overriding the method - now this widget is moved from add widget to main page, so we need to make sure the refresh happens from now
    processChangeToIconifiedState(isIcon: boolean) {
        if (this.refreshInterval && !isIcon) {
            this.setNextRefresh();
        }
    }
    // When the element is added, set the valid flag as true.  The timer will depend on this flag.  if true, it continues else stop the timer
    connectedCallback() {
        super.connectedCallback();
        this.valid = true;
    }

    // When the element is removed, set the valid flag as false.  The timer will depend on this flag.  if true, it continues else stop the timer
    disconnectedCallback() {
        this.valid = false;
        super.disconnectedCallback();
    }

    // Widget is loaded, so start the timer
    firstUpdated(changedProperties: PropertyValues) {
        this.setNextRefresh();
        this.showOrHideForState();
    }

    setNextRefresh() {
        // If already there, clear the timer
        if (this.timerHandler?.handle) {
            clearTimeout(this.timerHandler.handle);
            this.timerHandler.handle = 0;
        }

        // Set the timer only if there is a non zero refreshInterval
        if (this.refreshInterval) {
            var that = this;
            if (!this.timerHandler) {
                this.timerHandler = {};
            }

            // Save the handle to enable stopping when required
            this.timerHandler.handle = setTimeout(function (event) { that.refresh(event); }, Number(this.refreshInterval));
        }
    }

    // Takes care of auto refreshing the contents.
    refresh(event) {
        if (this.iconified) {
            return;
        }
        this.timerHandler.handle = 0;
        if (this.valid) {
            // Check if widget is still in the DOM
            // Get the content loader from shadow root and reload it with the current url
            var content = this.shadowRoot?.querySelector("landing-page-content-loader") as LandingPageContentLoader;
            content.reload();

            // After reload, set the timer again
            this.setNextRefresh();

            // increment the counter - for debugging only
            this.refreshCount = Number(this._refreshCount) + 1;
        } else {
            // Widget is removed from the DOM, so no need to reload or set timer again!
            console.log("Widget " + this.widgetId + " is removed from dom, so stoping the timer!")
        }
    }

    // Takes care of auto refreshing the contents.
    reloadWidget(event) {
        event.stopPropagation();
        event.preventDefault();

        if (this.iconified) {
            return;
        }

        //cancel the timer if exists
        if (this.timerHandler?.handle)
            clearTimeout(this.timerHandler.handle);

        // Get the content loader from shadow root and reload it with the current url
        var content = this.shadowRoot?.querySelector("landing-page-content-loader") as LandingPageContentLoader;
        content.reload();

        // After reload, set the timer again
        this.setNextRefresh();

        // increment the counter - for debugging only
        this.refreshCount = Number(this._refreshCount) + 1;
    }

    // The title bar which is the handle for jQuery UI sortable API
    getHandle() {
        return html`<div id="widgetbox-header">
      <p class="float-left widget-header-title">${this.getWidgetTitle()}</p>

      <!-- Just for debugging, this displays the widget id.  Can be overridden if there is a need to display more info -->
      ${this.getWidgetIdForDebug()}

      <a href="#" class="widget-refresh-icon float-right" @click="${(e: Event) => this.reloadWidget(e)}">&nbsp;</a>
      <div class="clearfix"></div>
    </div>`;
    }

    // Overriding debug part of the widget.render().  If there is a refresh interval set, the debug info also displays the count of refresh
    getWidgetIdForDebug() {
        return html`<span class= "absolute_hidden">${this.widgetId}${this._refreshInterval ? html`&nbsp;(${this.refreshCount})` : html``}</span>`;
    }

    // Overriding content part of the widget.render().  We need landing-page-content-loader to load contents from a URL
    getContentLoaderSlot() {
        return html`
            <div class="widget-content-class">
                <landing-page-content-loader widgetId="${this.widgetId}" url="${this.contentUrl}" >landing-page template content</landing-page-content-loader>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'data-insights-widget': DataInsightsWidget;
    }
}
